import React, { useState, useEffect, useCallback } from 'react';
import { Table, Card, Statistic, Progress, Tag, Button, Spin, Empty } from 'antd';
import { SyncOutlined, DollarOutlined } from '@ant-design/icons';
import { fetchBucketStats, formatBytes } from '../services';
import type { BucketStatsResponse, BucketFileInfo, BucketCategoryStats } from '../services';

const CATEGORY_COLORS: Record<string, string> = {
  'Bug 截圖': '#ff4d4f',
  '圖片': '#1677ff',
  'PDF': '#cf1322',
  '簡報': '#d4380d',
  '試算表': '#52c41a',
  'Word 文件': '#2f54eb',
  '文件': '#722ed1',
  '其他': '#bfbfbf',
};

const GCSBucketPanel: React.FC = () => {
  const [data, setData] = useState<BucketStatsResponse | null>(null);
  const [loading, setLoading] = useState(false);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetchBucketStats();
      setData(res);
    } catch (err) {
      console.error('Fetch bucket stats error:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { reload(); }, [reload]);

  const categoryColumns = [
    {
      title: '類別', dataIndex: 'category', key: 'category', width: 140,
      render: (v: string) => <Tag color={CATEGORY_COLORS[v] || 'default'}>{v}</Tag>,
    },
    { title: '檔案數', dataIndex: 'fileCount', key: 'fileCount', width: 100, align: 'right' as const },
    {
      title: '容量', dataIndex: 'totalBytes', key: 'totalBytes', width: 120, align: 'right' as const,
      render: (v: number) => formatBytes(v),
    },
    {
      title: '佔比', key: 'percent', width: 200,
      render: (_: unknown, r: BucketCategoryStats) => {
        const pct = data && data.totalBytes > 0 ? (r.totalBytes / data.totalBytes) * 100 : 0;
        return <Progress percent={Math.round(pct)} size="small" strokeColor={CATEGORY_COLORS[r.category] || '#1677ff'} />;
      },
    },
  ];

  const fileColumns = [
    {
      title: '檔名', dataIndex: 'name', key: 'name', ellipsis: true,
      render: (v: string) => <span className="font-mono text-xs">{v}</span>,
    },
    {
      title: '類別', dataIndex: 'category', key: 'category', width: 100,
      render: (v: string) => <Tag color={CATEGORY_COLORS[v] || 'default'} style={{ margin: 0 }}>{v}</Tag>,
    },
    {
      title: '上傳者', dataIndex: 'uploadedByName', key: 'uploadedByName', width: 100,
      render: (v: string, r: BucketFileInfo) => (
        <span className="text-xs">{v || r.uploadedBy || <span className="text-gray-300">—</span>}</span>
      ),
    },
    {
      title: '大小', dataIndex: 'size', key: 'size', width: 100, align: 'right' as const,
      render: (v: number) => <span className="text-xs">{formatBytes(v)}</span>,
    },
    {
      title: '上傳時間', dataIndex: 'created', key: 'created', width: 160,
      render: (v: string) => <span className="text-xs text-gray-500">{v}</span>,
    },
  ];

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-base font-semibold text-gray-700">GCS Bucket 使用量</h3>
        <Button icon={<SyncOutlined spin={loading} />} size="small" onClick={reload}>刷新</Button>
      </div>

      {loading && !data ? (
        <div className="flex justify-center py-12"><Spin /></div>
      ) : data ? (
        <>
          {/* Summary Cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            <Card size="small">
              <Statistic title="Bucket" value={data.bucketName} valueStyle={{ fontSize: 16, fontFamily: 'monospace' }} />
            </Card>
            <Card size="small">
              <Statistic title="總檔案數" value={data.totalFiles} suffix="個" />
            </Card>
            <Card size="small">
              <Statistic title="總容量" value={formatBytes(data.totalBytes)} valueStyle={{ fontSize: 20 }} />
            </Card>
            <Card size="small">
              <Statistic
                title="預估月費"
                prefix={<DollarOutlined />}
                value={data.monthlyCostUSD < 0.01 ? '< 0.01' : data.monthlyCostUSD.toFixed(4)}
                suffix="USD"
                valueStyle={{ color: data.monthlyCostUSD > 1 ? '#ff4d4f' : '#52c41a', fontSize: 20 }}
              />
            </Card>
          </div>

          {/* Cost Breakdown */}
          <Card size="small" className="mb-6" title="費用明細（預估/月）">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {Object.entries(data.costBreakdown).map(([key, val]) => {
                const labels: Record<string, string> = { storage: '儲存費', classA: '寫入操作', classB: '讀取操作', network: '網路出流量' };
                return (
                  <div key={key} className="text-center">
                    <div className="text-xs text-gray-400">{labels[key] || key}</div>
                    <div className="text-sm font-mono font-bold text-gray-700">
                      ${val < 0.001 ? '< 0.001' : val.toFixed(4)}
                    </div>
                  </div>
                );
              })}
            </div>
          </Card>

          {/* Category Breakdown */}
          <Card size="small" className="mb-6" title="檔案類型分布">
            <Table<BucketCategoryStats>
              columns={categoryColumns}
              dataSource={data.categories}
              rowKey="category"
              size="small"
              pagination={false}
            />
          </Card>

          {/* Recent Files */}
          <Card size="small" title={`最近上傳（${Math.min(20, data.totalFiles)} 筆）`}>
            <Table<BucketFileInfo>
              columns={fileColumns}
              dataSource={data.recentFiles}
              rowKey="name"
              size="small"
              pagination={false}
              scroll={{ x: 600 }}
            />
          </Card>
        </>
      ) : (
        <Empty description="無法取得 Bucket 資訊" />
      )}
    </div>
  );
};

export default GCSBucketPanel;
